import React, { useState, useEffect, useCallback, useRef } from 'react'
import { ChannelList, useChatContext } from 'stream-chat-react'
import SearchIcon from 'components/Common/SvgIcons/SearchIcon'
import { MessagingChannelList, MessagingChannelPreview } from 'components/Chat/components'
import { useUnreadContext } from 'components/Chat/contexts/UnreadContext'
import { Channel } from 'stream-chat'
import { DefaultStreamChatGenerics } from 'components/Chat/types'
import { useTenantId } from 'utils/useTenantId'
import { ampli } from 'ampli'
import UserGroupIcon from 'assets/svgs/user-group.svg'
import PencilSquareIcon from 'assets/svgs/pencil-square.svg'

export const getTotalChatUnreadCount = (channels: Channel<DefaultStreamChatGenerics>[]) => {
  return channels.map((channel) => channel.countUnread()).reduce((total, count) => total + count, 0)
}

const ChannelListHeader = ({
  userId,
  usersAssessmentResponseByQuestion,
  setShowChannelCreate,
  showChannelCreate,
  setShowChannelPreview,
  showChannelPreview,
  chatUnreadMessageCountAction,
  chatUnreadMessageCount,
  setShowUserSelection,
  showUserSelection,
  setShowGroupCreation,
  showGroupCreation,
  isSelectingChannel = false,
}) => {
  const tenantId = useTenantId()
  const { channel: activeChannel, setActiveChannel, client } = useChatContext()

  const { setChatsUnreadCount, channelMessageCount, setChannelMessageCount } = useUnreadContext()
  const [channelFilters]: any = useState({
    type: 'messaging',
    $and: [{ team: { $in: [tenantId] } }, { members: { $in: [userId] } }],
  })

  const [searchChannel, setSearchChannel] = useState('')
  const [channelMatchingMessages, setChannelMatchingMessages] = useState<Map<string, any>>(
    new Map()
  )
  const [isSearching, setIsSearching] = useState(false)
  const didInitialAutoSelectRef = useRef(false)

  // Function to search messages across all channels
  const searchMessages = useCallback(
    async (query: string) => {
      if (!query.trim() || !client) {
        setChannelMatchingMessages(new Map())
        return
      }

      setIsSearching(true)
      try {
        // Search for messages containing the query
        // Use $autocomplete operator for partial word matching (e.g., "abc" matches "abcd")
        const searchResponse = await client.search(
          {
            type: 'messaging',
            $and: [{ team: { $in: [tenantId] } }, { members: { $in: [userId] } }],
          },
          {
            text: { $autocomplete: query.trim() },
          },
          {
            limit: 50,
          }
        )

        const results = searchResponse.results || []

        // Create a map of channel IDs to their matching messages
        const channelMessageMap = new Map()
        results.forEach((result) => {
          if (result.message && result.message.cid) {
            const channelId = result.message.cid
            if (!channelMessageMap.has(channelId)) {
              channelMessageMap.set(channelId, [])
            }
            channelMessageMap.get(channelId).push(result.message)
          }
        })

        setChannelMatchingMessages(channelMessageMap)
      } catch (error) {
        console.error('Error searching messages:', error)
        setChannelMatchingMessages(new Map())
      } finally {
        setIsSearching(false)
      }
    },
    [client, tenantId, userId]
  )

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchChannel) {
        searchMessages(searchChannel)
      } else {
        setChannelMatchingMessages(new Map())
      }
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchChannel, searchMessages])

  const customRenderFilter = (channels) => {
    try {
      setChatsUnreadCount(getTotalChatUnreadCount(channels))

      channels.forEach((channel) => (channelMessageCount[channel.cid] = channel.countUnread()))
      setChannelMessageCount(channelMessageCount)

      // If we have a search query and search results, filter channels to only show those with matching messages
      let filteredChannels = channels
      if (searchChannel.trim() && channelMatchingMessages.size > 0) {
        filteredChannels = channels.filter((channel) => channelMatchingMessages.has(channel.cid))
      }

      // One-time initial auto-select on page load if no active channel and not in special modes
      if (
        !didInitialAutoSelectRef.current &&
        filteredChannels.length > 0 &&
        !activeChannel &&
        !showChannelCreate &&
        !showUserSelection &&
        !showGroupCreation &&
        !isSelectingChannel
      ) {
        didInitialAutoSelectRef.current = true
        setTimeout(() => {
          if (
            !activeChannel &&
            !showChannelCreate &&
            !showUserSelection &&
            !showGroupCreation &&
            !isSelectingChannel
          ) {
            setActiveChannel(filteredChannels[0])
            setShowChannelPreview(true)
          }
        }, 50)
      }

      // Ensure chat preview is shown when we have channels (unless creating new channel)
      if (filteredChannels.length > 0 && !showChannelCreate && !showChannelPreview) {
        setShowChannelPreview(true)
      }

      return filteredChannels
    } catch (err) {
      return channels
    }
  }

  const searchQueryHandler = (e) => {
    // Search is now handled by the useEffect with debouncing
    // This function can be removed or used for additional Enter key handling if needed
  }

  const handleNewMessage = () => {
    setShowUserSelection?.(true)
    setShowChannelCreate?.(false)
    setShowGroupCreation?.(false)
    ampli.newMessageInitiated()
  }

  const handleCreateGroup = () => {
    setShowGroupCreation?.(true)
    setShowChannelCreate?.(false)
    setShowUserSelection?.(false)
  }

  // Show user selection or group creation UI
  if (showUserSelection || showGroupCreation) {
    return null // Let parent handle these views
  }

  return (
    <div className="h-full bg-white flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-app-grey-25 h-[70px]">
        <h1 className="text-xl font-semibold text-app-black-100 mb-0">Conversations</h1>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-3 border-b border-app-grey-25">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-app-grey-55" />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchChannel}
            onChange={(e) => {
              setSearchChannel(e.target.value)
            }}
            onKeyPress={searchQueryHandler}
            className="w-full pl-12 pr-4 py-2.5 bg-app-grey-20 border border-app-grey-25 rounded-full text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
          />
          {isSearching && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-app-primary-60"></div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="py-3 border-b border-app-grey-25">
        <button
          onClick={handleNewMessage}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left text-app-black-100 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
        >
          <span className="font-medium text-sm">New message</span>
          <div className="w-6 h-6 flex items-center justify-center">
            <img src={UserGroupIcon} alt="new-message" className="w-5 h-5" />
          </div>
        </button>

        <div className="my-1.5 h-px bg-app-grey-25" />

        <button
          onClick={handleCreateGroup}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left text-app-black-100 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
        >
          <span className="font-medium text-sm">Create a group</span>
          <div className="w-6 h-6 flex items-center justify-center">
            <img src={PencilSquareIcon} alt="create-group" className="w-5 h-5" />
          </div>
        </button>
      </div>

      {/* Channel List */}
      <div className="flex-1 overflow-hidden">
        <ChannelList
          channelRenderFilterFn={customRenderFilter}
          filters={channelFilters}
          List={MessagingChannelList}
          setActiveChannelOnMount={false}
          Preview={(props) => (
            <MessagingChannelPreview
              {...(props as any)}
              searchChannel={searchChannel}
              showChannelCreate={showChannelCreate}
              setShowChannelCreate={setShowChannelCreate}
              setShowChannelPreview={setShowChannelPreview}
              usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion}
              chatUnreadMessageCountAction={chatUnreadMessageCountAction}
              channelMatchingMessages={channelMatchingMessages}
              isSearching={isSearching}
            />
          )}
        />
      </div>
    </div>
  )
}

export default ChannelListHeader
